import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ChangeDetectionStrategy, Component, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, UntypedFormControl} from '@angular/forms';
import {isEqual, pull} from 'lodash';
import {Observable, Subject, timer} from 'rxjs';
import {map, startWith, switchMap} from 'rxjs/operators';

enum ContainerTypes {
  AVAILABLE = 'available',
  SELECTED = 'selected'
}

@Component({
  selector: 'items-selector',
  templateUrl: './items-selector.component.html',
  styleUrls: ['./items-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemsSelectorComponent implements OnInit {
  control: UntypedFormControl | AbstractControl;
  DEBOUNCE_TIME = 150;

  @Input() availableItemsTitle: string;
  @Input() selectedItemsTitle: string;
  @Input() displayValueField: string;
  @Input() availableItems: any[] = [];
  @Input() selectedItems: any[] = [];
  @Input() isReadonly = false;
  @Input() showSelectAll: boolean;
  @Input() filterPlaceholder: string;
  @Output() validateSelectedItems$: Subject<boolean> = new Subject<boolean>();

  public container = ContainerTypes;
  public isSelectedAll = false;
  public filteredItems$: Observable<any[]>;
  private filterValue: string;
  public filteredAvailableItems: any[] = [];

  constructor() {}

  ngOnInit() {
    if (this.availableItems.length === 0) {
      this.isSelectedAll = true;
    }
    this.subscribeToFilterChanges();
  }

  public drop($event: CdkDragDrop<any>) {
    const {container, previousContainer, previousIndex, currentIndex} = $event;
    if (previousContainer === container) {
      moveItemInArray(container.data, previousIndex, currentIndex);
    } else {
      if (
        previousContainer.id === 'available-items-drop-list' &&
        container.id === 'selected-items-drop-list'
      ) {
        const itemToMove = this.filteredAvailableItems[previousIndex];
        const originalIndex = this.availableItems.findIndex(item => isEqual(item, itemToMove));
        if (originalIndex !== -1) {
          this.availableItems.splice(originalIndex, 1);
          this.selectedItems.splice(currentIndex, 0, itemToMove);
        }
      } else if (
        previousContainer.id === 'selected-items-drop-list' &&
        container.id === 'available-items-drop-list'
      ) {
        const itemToMove = this.selectedItems[previousIndex];
        const originalIndex = this.selectedItems.findIndex(item => isEqual(item, itemToMove));
        if (originalIndex !== -1) {
          this.selectedItems.splice(originalIndex, 1);
          let insertIndex = 0;
          if (this.filteredAvailableItems.length > 0 && currentIndex > 0) {
            const beforeItem = this.filteredAvailableItems[currentIndex - 1];
            insertIndex = this.availableItems.findIndex(item => isEqual(item, beforeItem)) + 1;
          }
          this.availableItems.splice(insertIndex, 0, itemToMove);
        }
      } else if (
        container.id === 'available-items-drop-list' &&
        previousContainer.id === 'available-items-drop-list'
      ) {
        const itemToMove = this.filteredAvailableItems[previousIndex];
        const fromIndex = this.availableItems.findIndex(item => isEqual(item, itemToMove));
        const targetItem = this.filteredAvailableItems[currentIndex];
        const toIndex = this.availableItems.findIndex(item => isEqual(item, targetItem));
        if (fromIndex !== -1 && toIndex !== -1) {
          this.availableItems.splice(fromIndex, 1);
          this.availableItems.splice(toIndex, 0, itemToMove);
        }
      } else if (
        container.id === 'selected-items-drop-list' &&
        previousContainer.id === 'selected-items-drop-list'
      ) {
        moveItemInArray(this.selectedItems, previousIndex, currentIndex);
      }
    }

    const lockedColumnIndex = container.data.findIndex(column => column.locked);

    if (lockedColumnIndex !== -1) {
      const lockedColumn = container.data[lockedColumnIndex];

      const unlockedColumns = [
        lockedColumn,
        ...container.data.slice(0, lockedColumnIndex),
        ...container.data.slice(lockedColumnIndex + 1)
      ];

      container.data.splice(0, container.data.length, ...unlockedColumns);
    }

    this.validateSelectedItems$.next(true);
    this.isSelectedAll = !this.availableItems.length;
    this.control.setValue(this.filterValue);
  }

  public handleDblclick(item, container) {
    if (item.locked || this.isReadonly) {
      return;
    }

    switch (container) {
      case ContainerTypes.AVAILABLE:
        this.handleSelectByDblclick(item, this.availableItems, this.selectedItems);
        break;
      case ContainerTypes.SELECTED:
        this.handleSelectByDblclick(item, this.selectedItems, this.availableItems);
        break;
    }

    this.validateSelectedItems$.next(true);
  }

  public handleSelectByDblclick(item, previousContainer, currentContainer) {
    this.pushItemInContainer(item, currentContainer);
    this.pullItemFromContainer(item, previousContainer);
    this.validateSelectedItems$.next(true);
  }

  public toggleSelectAll() {
    this.isSelectedAll = !this.isSelectedAll;

    if (this.isSelectedAll) {
      this.availableItems.forEach(item =>
        item.locked ? null : this.pushItemInContainer(item, this.selectedItems)
      );

      pull(this.availableItems, ...this.selectedItems);
    } else {
      this.selectedItems.forEach(item =>
        item.locked ? null : this.pushItemInContainer(item, this.availableItems)
      );

      pull(this.selectedItems, ...this.availableItems);
    }
    this.validateSelectedItems$.next(true);
    this.resetFilter();
  }

  private pushItemInContainer(item, container) {
    container.push(item);
  }

  private pullItemFromContainer(item, container: any[]) {
    const index = container.findIndex(i => isEqual(i, item));
    container.splice(index, 1);
    this.isSelectedAll = !this.availableItems.length;
    this.control.setValue(this.filterValue);
  }

  private subscribeToFilterChanges() {
    this.control = new FormControl('');

    this.filteredItems$ = this.control.valueChanges.pipe(
      startWith(''),
      switchMap(value => {
        const query = value?.trim() ?? '';
        return timer(query ? this.DEBOUNCE_TIME : 0).pipe(map(() => this.filterItems(query)));
      })
    );
    this.filteredItems$.subscribe(items => {
      this.filteredAvailableItems = items;
    });
  }

  private filterItems(value: string) {
    this.filterValue = value.toLowerCase();

    return this.availableItems.filter(item =>
      item.displayValue
        ? item.displayValue.toLowerCase().includes(this.filterValue)
        : item?.toLowerCase().includes(this.filterValue)
    );
  }

  public resetFilter() {
    this.control.setValue('');
  }
}
